const isSamMinimumOrderNoticeText = (text) => {
  if (typeof text !== 'string') return false;
  const normalizedText = text.replace(/\s+/g, ' ').trim();
  return /^(?:you've reached\s+)?min\.?\s*orders?(?:\s+\$\d+(?:[.,]\d{2})?)?$/i.test(normalizedText);
};

const SamCommerce = Object.freeze({
  getStickyPurchaseState: ({ hasSeenPrimary, primaryIsVisible, disabled }) => ({
    hidden: !hasSeenPrimary || primaryIsVisible,
    disabled: Boolean(disabled),
  }),
  canRevealMarketingPopup: ({ isProductPage, purchaseEngaged }) => !isProductPage || !purchaseEngaged,
  isMinimumOrderNoticeText: isSamMinimumOrderNoticeText,
});

window.SamCommerce = SamCommerce;

const initSamFaq = (root = document) => {
  root.querySelectorAll('[data-sam-faq]').forEach((container) => {
    if (container.dataset.samReady === 'true') return;

    const schemaItems = [];

    container.querySelectorAll('.faq-item').forEach((item, index) => {
      const question = item.querySelector('.faq-question, h2, h3, h4');
      const answer = item.querySelector('.faq-answer') || question?.nextElementSibling;
      if (!question || !answer) return;

      const id = `SamFaq-${container.dataset.sectionId}-${index}`;
      const button = question.matches('button') ? question : document.createElement('button');

      if (button !== question) {
        button.innerHTML = question.innerHTML;
        question.replaceWith(button);
      }

      button.type = 'button';
      button.className = 'faq-question';
      button.setAttribute('aria-expanded', 'false');
      button.setAttribute('aria-controls', id);

      answer.id = id;
      answer.classList.add('faq-answer');
      answer.hidden = true;

      button.addEventListener('click', () => {
        const expanded = button.getAttribute('aria-expanded') === 'true';
        button.setAttribute('aria-expanded', String(!expanded));
        answer.hidden = expanded;
      });

      schemaItems.push({
        '@type': 'Question',
        name: button.textContent.trim(),
        acceptedAnswer: {
          '@type': 'Answer',
          text: answer.textContent.trim(),
        },
      });
    });

    if (schemaItems.length && !container.querySelector('script[type="application/ld+json"]')) {
      const script = document.createElement('script');
      script.type = 'application/ld+json';
      script.textContent = JSON.stringify({
        '@context': 'https://schema.org',
        '@type': 'FAQPage',
        mainEntity: schemaItems,
      });
      container.append(script);
    }

    container.dataset.samReady = 'true';
  });
};


const initSamProductPurchase = (root = document) => {
  root.querySelectorAll('[data-sam-sticky-purchase]').forEach((stickyPurchase) => {
    if (stickyPurchase.dataset.samPurchaseReady === 'true') return;

    const sectionId = stickyPurchase.dataset.sectionId;
    const canonicalButton = document.getElementById(`ProductSubmitButton-${sectionId}`);
    const stickyButton = stickyPurchase.querySelector('[data-sam-sticky-submit]');
    if (!canonicalButton || !stickyButton) return;

    let hasSeenPrimary = false;
    const updateVisibility = (primaryIsVisible) => {
      if (primaryIsVisible) hasSeenPrimary = true;
      const state = SamCommerce.getStickyPurchaseState({
        hasSeenPrimary,
        primaryIsVisible,
        disabled: canonicalButton.disabled,
      });

      stickyPurchase.hidden = state.hidden;
      stickyButton.disabled = state.disabled;
      document.body.classList.toggle('sam-sticky-purchase-visible', !state.hidden);
    };

    const observer = new IntersectionObserver(([entry]) => updateVisibility(entry.isIntersecting), {
      threshold: 0.15,
    });
    observer.observe(canonicalButton);

    stickyButton.addEventListener('click', () => canonicalButton.click());

    if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
      let unsubscribe;
      const syncVariantPurchase = (event) => {
        if (!stickyPurchase.isConnected) {
          unsubscribe?.();
          return;
        }
        if (event.data.sectionId !== sectionId) return;

        const sourcePrice = event.data.html.querySelector('[data-sam-submit-price]');
        let canonicalPrice = canonicalButton.querySelector?.('[data-sam-submit-price]');
        const stickyPrice = stickyPurchase.querySelector('[data-sam-sticky-price]');
        const canonicalLabel = canonicalButton.querySelector?.('span');
        const stickyLabel = stickyButton.querySelector?.('span');

        if (sourcePrice) {
          if (!canonicalPrice) {
            canonicalPrice = sourcePrice.cloneNode(true);
            canonicalLabel?.insertAdjacentElement('afterend', canonicalPrice);
          } else {
            canonicalPrice.innerHTML = sourcePrice.innerHTML;
          }
          canonicalPrice.hidden = false;
          if (stickyPrice) {
            stickyPrice.textContent = sourcePrice.textContent.replace(/^\s*·\s*/, '').trim();
            stickyPrice.hidden = false;
          }
        } else {
          if (canonicalPrice) canonicalPrice.hidden = true;
          if (stickyPrice) stickyPrice.hidden = true;
        }

        if (canonicalLabel && stickyLabel) stickyLabel.textContent = canonicalLabel.textContent.trim();
        stickyButton.disabled = canonicalButton.disabled;
      };

      unsubscribe = subscribe(PUB_SUB_EVENTS.variantChange, syncVariantPurchase);
    }
    stickyPurchase.dataset.samPurchaseReady = 'true';
  });
};

const initSamMarketingPopup = () => {
  const isProductPage = Boolean(document.querySelector('main[data-template="product"]'));
  let purchaseEngaged = false;
  let revealed = false;
  let observer;
  let engagementSentinel;

  if (isProductPage) document.documentElement.classList.remove('sam-marketing-ready');

  const reveal = () => {
    if (revealed || !SamCommerce.canRevealMarketingPopup({ isProductPage, purchaseEngaged })) return;
    revealed = true;
    observer?.disconnect?.();
    engagementSentinel?.remove();
    document.documentElement.classList.add('sam-marketing-ready');
    sessionStorage.setItem('samMarketingReady', 'true');
  };

  if (!isProductPage && sessionStorage.getItem('samMarketingReady') === 'true') {
    reveal();
    return;
  }

  window.setTimeout(reveal, isProductPage ? 45000 : 30000);

  if (isProductPage) {
    const suppressOnPurchaseEngagement = (event) => {
      const selector = event.type === 'change' ? 'variant-selects' : '[id^="ProductSubmitButton-"]';
      if (!event.target.closest?.(selector)) return;
      purchaseEngaged = true;
      observer?.disconnect?.();
      engagementSentinel?.remove();
    };
    document.addEventListener('change', suppressOnPurchaseEngagement, true);
    document.addEventListener('click', suppressOnPurchaseEngagement, true);

    engagementSentinel = document.createElement('span');
    engagementSentinel.setAttribute('aria-hidden', 'true');
    Object.assign(engagementSentinel.style, {
      position: 'absolute',
      top: 'calc(100vh + 1px)',
      insetInlineStart: '0',
      width: '1px',
      height: '1px',
      pointerEvents: 'none',
    });
    document.body.appendChild(engagementSentinel);

    observer = new IntersectionObserver(([entry]) => {
      if (!entry.isIntersecting && entry.boundingClientRect.top <= 0) reveal();
    });
    observer.observe(engagementSentinel);
    return;
  }

  const firstMainSection = document.querySelector('main > :first-child');
  if (!firstMainSection) return;
  observer = new IntersectionObserver(([entry]) => {
    if (!entry.isIntersecting && entry.boundingClientRect.bottom <= 0) reveal();
  });
  observer.observe(firstMainSection);
};


const initSamProductGallery = (root = document) => {
  if (!window.matchMedia('(min-width: 990px)').matches) return;

  root.querySelectorAll('media-gallery[data-desktop-layout="thumbnail_slider"]').forEach((gallery) => {
    if (gallery.dataset.samVerticalReady === 'true') return;

    const viewer = gallery.querySelector('[id^="GalleryViewer"]');
    const thumbnails = gallery.querySelector('[id^="GalleryThumbnails"]');
    const mediaItems = viewer?.querySelectorAll('[data-media-id]');
    if (!viewer || !thumbnails || !mediaItems || mediaItems.length < 2) return;

    const setActiveMedia = (mediaId) => {
      mediaItems.forEach((media) => media.classList.toggle('is-active', media.dataset.mediaId === mediaId));
      thumbnails.querySelectorAll('[data-target]').forEach((thumbnail) => {
        const button = thumbnail.querySelector('button');
        if (!button) return;
        if (thumbnail.dataset.target === mediaId) button.setAttribute('aria-current', 'true');
        else button.removeAttribute('aria-current');
      });
    };

    thumbnails.querySelectorAll('[data-target]').forEach((thumbnail) => {
      thumbnail.querySelector('button')?.addEventListener(
        'click',
        (event) => {
          event.preventDefault();
          event.stopImmediatePropagation();

          const mediaId = thumbnail.dataset.target;
          const media = viewer.querySelector(`[data-media-id="${CSS.escape(mediaId)}"]`);
          if (!media) return;

          setActiveMedia(mediaId);
          const top = media.getBoundingClientRect().top + window.scrollY - 20;
          const behavior = window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth';
          window.scrollTo({ top, behavior });
        },
        true
      );
    });

    const observer = new IntersectionObserver(
      (entries) => {
        const visible = entries
          .filter((entry) => entry.isIntersecting)
          .sort((left, right) => right.intersectionRatio - left.intersectionRatio)[0];
        if (visible) setActiveMedia(visible.target.dataset.mediaId);
      },
      { rootMargin: '-15% 0px -65% 0px', threshold: [0, 0.25, 0.5, 0.75] }
    );

    mediaItems.forEach((media) => observer.observe(media));
    gallery.dataset.samVerticalReady = 'true';
  });
};

const removeSamMinimumOrderNotice = (root = document) => {
  if (!(root instanceof Document || root instanceof Element)) return;

  const elements = root instanceof Element ? [root, ...root.querySelectorAll('span')] : root.querySelectorAll('span');
  [...elements].forEach((message) => {
    if (!message.isConnected || !SamCommerce.isMinimumOrderNoticeText(message.textContent)) return;

    const appContainer = message.closest('.loox-pn');
    if (appContainer) {
      appContainer.remove();
      return;
    }

    let banner = message.parentElement;
    while (banner && banner !== document.body && !banner.querySelector('button[aria-label="close"]')) {
      banner = banner.parentElement;
    }
    if (banner && banner !== document.body) banner.remove();
    else message.remove();
  });
};

const initSamMinimumOrderCleanup = () => {
  removeSamMinimumOrderNotice();
  const observer = new MutationObserver((records) => {
    records.forEach((record) => record.addedNodes.forEach(removeSamMinimumOrderNotice));
  });
  observer.observe(document.body, { childList: true, subtree: true });
};


const getSamSliderPositionForSlide = (host, slide) => {
  if (!host.track || !slide) return 0;
  const centeredPosition = slide.offsetLeft - (host.track.clientWidth - slide.clientWidth) / 2;
  const maximumPosition = Math.max(host.track.scrollWidth - host.track.clientWidth, 0);
  return Math.min(Math.max(centeredPosition, 0), maximumPosition);
};

const getSamSliderPosition = (host, index) => getSamSliderPositionForSlide(host, host.slides[index]);

const getSamLogicalIndex = (host, slide) => {
  const logicalIndex = Number(slide?.dataset.samLogicalIndex);
  if (Number.isInteger(logicalIndex)) return logicalIndex;
  return Math.max(host.slides.indexOf(slide), 0);
};

const getSamNearestLoopTarget = (host) => {
  const candidates = host.loopSlides?.length ? host.loopSlides : host.slides;
  let nearestSlide = candidates[0];
  let nearestDistance = Number.POSITIVE_INFINITY;
  candidates.forEach((slide) => {
    const distance = Math.abs(host.track.scrollLeft - getSamSliderPositionForSlide(host, slide));
    if (distance < nearestDistance) {
      nearestSlide = slide;
      nearestDistance = distance;
    }
  });

  const logicalIndex = getSamLogicalIndex(host, nearestSlide);
  const resetSlide = nearestSlide?.classList.contains('sam-slider-clone') ? host.slides[logicalIndex] : null;
  return { logicalIndex, slide: nearestSlide, resetSlide };
};

const getSamNearestSlideIndex = (host) => getSamNearestLoopTarget(host).logicalIndex;

const makeSamSliderClone = (slide, logicalIndex) => {
  const clone = slide.cloneNode(true);
  clone.classList.add('sam-slider-clone');
  clone.classList.remove('is-active');
  clone.dataset.samLogicalIndex = String(logicalIndex);
  clone.removeAttribute('id');
  clone.removeAttribute('data-shopify-editor-block');
  clone.removeAttribute('aria-current');
  clone.setAttribute('aria-hidden', 'true');
  clone.setAttribute('inert', '');
  clone.setAttribute('role', 'presentation');
  clone.querySelectorAll('[id]').forEach((element) => element.removeAttribute('id'));
  clone.querySelectorAll('[data-shopify-editor-block]').forEach((element) => {
    element.removeAttribute('data-shopify-editor-block');
  });
  clone.querySelectorAll('[aria-current]').forEach((element) => element.removeAttribute('aria-current'));
  clone.querySelectorAll('img').forEach((image) => {
    image.loading = 'eager';
  });
  clone.querySelectorAll('a, button, input, select, textarea, [tabindex]').forEach((element) => {
    element.tabIndex = -1;
  });
  return clone;
};

const initSamSliderLoop = (host) => {
  if (!host.track || host.slides.length < 2 || host.loopSlides?.length) return;
  host.slides.forEach((slide, index) => {
    slide.dataset.samLogicalIndex = String(index);
  });
  if (host.loopPreloadImages) {
    host.slides.forEach((slide) => {
      slide.querySelectorAll('img').forEach((image) => {
        image.loading = 'eager';
      });
    });
  }
  const cloneCount = Math.min(
    Math.max(Number.isInteger(host.loopCloneCount) ? host.loopCloneCount : 1, 1),
    host.slides.length - 1,
  );
  const startOffset = host.slides.length - cloneCount;
  host.loopStartClones = host.slides
    .slice(startOffset)
    .map((slide, index) => makeSamSliderClone(slide, startOffset + index));
  host.loopEndClones = host.slides
    .slice(0, cloneCount)
    .map((slide, index) => makeSamSliderClone(slide, index));
  host.track.prepend(...host.loopStartClones);
  host.track.append(...host.loopEndClones);
  host.loopSlides = [...host.loopStartClones, ...host.slides, ...host.loopEndClones];
  host.loopReady = false;
  if (host.loopOriginalSnapType === undefined) {
    host.loopOriginalSnapType = host.track.style.scrollSnapType;
  }
  host.track.style.scrollSnapType = 'none';
  host.sliderComponent?.resetPages?.();
  const initializeLoopPosition = () => {
    if (!host.loopSlides?.length || !host.track || !host.slides[0]) return;
    const position = getSamSliderPosition(host, 0);
    if (position <= 0) return;
    host.track.scrollTo({ left: position, behavior: 'instant' });
    host.sliderComponent?.update?.();
    host.syncFromTrack?.(0);
    host.loopReady = true;
    host.track.style.scrollSnapType = host.loopOriginalSnapType || '';
    host.loopOriginalSnapType = undefined;
    window.removeEventListener('load', host.loopInitHandler);
    host.loopInitHandler = undefined;
    host.loopInitObserver?.disconnect();
    host.loopInitObserver = undefined;
  };
  host.loopInitHandler = initializeLoopPosition;
  host.loopInitObserver?.disconnect();
  host.loopInitObserver = new ResizeObserver(initializeLoopPosition);
  host.loopInitObserver.observe(host.track);
  if (document.readyState === 'complete') {
    initializeLoopPosition();
  } else {
    window.addEventListener('load', host.loopInitHandler, { once: true });
  }
};

const destroySamSliderLoop = (host) => {
  window.removeEventListener('load', host.loopInitHandler);
  host.loopInitHandler = undefined;
  host.loopInitObserver?.disconnect();
  host.loopInitObserver = undefined;
  window.cancelAnimationFrame(host.loopSnapFrame);
  window.clearTimeout(host.loopSnapTimer);
  window.clearTimeout(host.loopSettleTimer);
  host.loopSettleTimer = undefined;
  if (host.track && host.loopOriginalSnapType !== undefined) {
    host.track.style.scrollSnapType = host.loopOriginalSnapType;
  }
  host.loopStartClones?.forEach((clone) => clone.remove());
  host.loopEndClones?.forEach((clone) => clone.remove());
  host.slides.forEach((slide) => {
    delete slide.dataset.samLogicalIndex;
    delete slide.dataset.samPosition;
  });
  host.loopStartClones = undefined;
  host.loopEndClones = undefined;
  host.isLoopResetting = false;
  host.loopReady = false;
  host.loopSlides = undefined;
  host.sliderComponent?.resetPages?.();
};

const clearSamSliderFallback = (host) => {
  window.clearTimeout(host.controlFallbackTimer);
  host.controlFallbackTimer = undefined;
  host.pendingSlideIndex = undefined;
  host.pendingSlidePosition = undefined;
  host.pendingResetPosition = undefined;
};

const clearSamSliderControlQueue = (host) => {
  host.pendingControlDirections = [];
};

const resetSamSliderPosition = (host, position, afterReset) => {
  if (!host.track || !Number.isFinite(position)) {
    afterReset?.();
    return;
  }

  host.cancelSlideAnimation?.();
  host.isLoopResetting = true;
  window.cancelAnimationFrame(host.loopSnapFrame);
  window.clearTimeout(host.loopSnapTimer);
  if (host.loopOriginalSnapType === undefined) {
    host.loopOriginalSnapType = host.track.style.scrollSnapType;
  }
  host.track.style.scrollSnapType = 'none';
  host.track.scrollTo({ left: position, behavior: 'instant' });
  host.sliderComponent?.update?.();
  let restored = false;
  const restoreSnap = () => {
    if (restored) return;
    restored = true;
    window.cancelAnimationFrame(host.loopSnapFrame);
    window.clearTimeout(host.loopSnapTimer);
    host.loopSnapFrame = undefined;
    host.loopSnapTimer = undefined;
    if (host.track) host.track.style.scrollSnapType = host.loopOriginalSnapType || '';
    host.loopOriginalSnapType = undefined;
    host.isLoopResetting = false;
    afterReset?.();
  };
  host.loopSnapFrame = window.requestAnimationFrame(restoreSnap);
  host.loopSnapTimer = window.setTimeout(restoreSnap, 64);
};

const syncSamSliderControls = (host) => {
  if (host.sliderComponent) host.sliderComponent.enableSliderLooping = true;
  const disabled = host.slides.length < 2;
  host.controlButtons?.forEach((button) => {
    button.disabled = disabled;
  });
};

const runSamSliderQueuedControl = (host) => {
  const direction = host.pendingControlDirections?.shift();
  if (direction) moveSamSliderByDirection(host, direction);
};

const finishSamSliderMovement = (host, logicalIndex, resetPosition) => {
  const finish = () => {
    clearSamSliderFallback(host);
    host.syncFromTrack?.(logicalIndex);
    syncSamSliderControls(host);
    runSamSliderQueuedControl(host);
  };
  if (Number.isFinite(resetPosition)) {
    resetSamSliderPosition(host, resetPosition, finish);
  } else {
    finish();
  }
};

const settleSamSliderLoop = (host) => {
  if (!host.track || !host.loopSlides?.length || host.isPointerDown || host.isDragging) return;
  const target = getSamNearestLoopTarget(host);
  host.syncFromTrack?.(target.logicalIndex);
  if (!target.resetSlide) return;
  resetSamSliderPosition(host, getSamSliderPositionForSlide(host, target.resetSlide), () => {
    host.syncFromTrack?.(target.logicalIndex);
    syncSamSliderControls(host);
    runSamSliderQueuedControl(host);
  });
};

const scheduleSamSliderSettle = (host) => {
  window.clearTimeout(host.loopSettleTimer);
  if (host.isPointerDown || host.isDragging) return;
  host.loopSettleTimer = window.setTimeout(() => settleSamSliderLoop(host), 120);
};

const handleSamSliderScroll = (host) => {
  if (!host.track || host.loopReady === false) return;
  const target = getSamNearestLoopTarget(host);
  host.syncFromTrack?.(target.logicalIndex);
  scheduleSamSliderSettle(host);

  if (!Number.isInteger(host.pendingSlideIndex)) {
    syncSamSliderControls(host);
    return;
  }
  if (Math.abs(host.track.scrollLeft - host.pendingSlidePosition) > 2) return;

  finishSamSliderMovement(host, host.pendingSlideIndex, host.pendingResetPosition);
};

const moveSamSliderTo = (host, index, position, behavior) => {
  if (typeof host.setSlidePosition === 'function') {
    host.setSlidePosition(index, position, behavior);
    return;
  }
  host.track.scrollTo({ left: position, behavior });
};

const moveSamSliderToTarget = (host, target, behavior) => {
  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const finalPosition = target.resetSlide
    ? getSamSliderPositionForSlide(host, target.resetSlide)
    : getSamSliderPositionForSlide(host, target.slide);
  const targetPosition = reducedMotion ? finalPosition : getSamSliderPositionForSlide(host, target.slide);
  host.pendingSlideIndex = target.logicalIndex;
  host.pendingSlidePosition = targetPosition;
  host.pendingResetPosition = reducedMotion || !target.resetSlide ? undefined : finalPosition;
  syncSamSliderControls(host);
  window.clearTimeout(host.controlFallbackTimer);

  if (reducedMotion || behavior === 'instant') {
    finishSamSliderMovement(host, target.logicalIndex, finalPosition);
    return;
  }

  moveSamSliderTo(host, target.logicalIndex, targetPosition, behavior);
  host.controlFallbackTimer = window.setTimeout(() => {
    if (host.pendingSlideIndex !== target.logicalIndex) return;
    finishSamSliderMovement(host, target.logicalIndex, finalPosition);
  }, host.controlFallbackDelay || 500);
};

const moveSamSliderByDirection = (host, direction) => {
  const currentIndex = getSamNearestSlideIndex(host);
  const targetIndex = (currentIndex + direction + host.slides.length) % host.slides.length;
  const wrapsForward = direction > 0 && currentIndex === host.slides.length - 1;
  const wrapsBackward = direction < 0 && currentIndex === 0;
  const targetSlide = wrapsForward
    ? host.loopEndClones?.[0]
    : wrapsBackward
      ? host.loopStartClones?.[host.loopStartClones.length - 1]
      : host.slides[targetIndex];
  const resetSlide = wrapsForward || wrapsBackward ? host.slides[targetIndex] : null;
  moveSamSliderToTarget(host, { logicalIndex: targetIndex, slide: targetSlide, resetSlide }, 'smooth');
};

const handleSamSliderControl = (host, event) => {
  const button = event.target.closest('button[name="previous"], button[name="next"]');
  if (!button || !host.contains(button)) return;
  event.preventDefault();
  event.stopImmediatePropagation();
  if (!host.track || host.slides.length < 2) return;
  if (host.loopReady === false) return;

  const direction = button.name === 'next' ? 1 : -1;
  scheduleSamSliderAutoplay(host);
  if (host.isLoopResetting || Number.isInteger(host.pendingSlideIndex)) {
    host.pendingControlDirections ||= [];
    host.pendingControlDirections.push(direction);
    return;
  }
  moveSamSliderByDirection(host, direction);
};

const initSamSliderNavigation = (host) => {
  if (host.sliderComponent) host.sliderComponent.enableSliderLooping = true;
  host.controlButtons = [...host.querySelectorAll('button[name="previous"], button[name="next"]')];
  host.handleControlClick = (event) => handleSamSliderControl(host, event);
  host.handleTrackScroll = () => handleSamSliderScroll(host);
  host.handleTrackTakeover = () => {
    host.cancelSlideAnimation?.();
    const takeoverPosition = host.track?.scrollLeft;
    clearSamSliderFallback(host);
    clearSamSliderControlQueue(host);
    if (Number.isFinite(takeoverPosition)) {
      host.track.scrollTo({ left: takeoverPosition, behavior: 'instant' });
      host.sliderComponent?.update?.();
    }
    syncSamSliderControls(host);
  };
  host.handleTrackPointerDown = (event) => {
    if (event.isPrimary === false) return;
    host.isPointerDown = true;
    host.isDragging = false;
    host.trackPointerId = event.pointerId;
    host.autoplayPausedByPointer = true;
    pauseSamSliderAutoplay(host);
    host.handleTrackTakeover();
  };
  host.handleTrackPointerMove = (event) => {
    if (!host.isPointerDown || event.pointerId !== host.trackPointerId) return;
    host.isDragging = true;
  };
  host.handleTrackPointerEnd = (event) => {
    if (event.pointerId !== host.trackPointerId) return;
    host.isPointerDown = false;
    host.isDragging = false;
    host.trackPointerId = undefined;
    host.autoplayPausedByPointer = false;
    scheduleSamSliderSettle(host);
    scheduleSamSliderAutoplay(host);
  };
  host.addEventListener('click', host.handleControlClick, true);
  host.track?.addEventListener('scroll', host.handleTrackScroll);
  host.track?.addEventListener('pointerdown', host.handleTrackPointerDown);
  host.track?.addEventListener('pointermove', host.handleTrackPointerMove);
  host.track?.addEventListener('wheel', host.handleTrackTakeover);
  window.addEventListener('pointerup', host.handleTrackPointerEnd);
  window.addEventListener('pointercancel', host.handleTrackPointerEnd);
  initSamSliderLoop(host);
  syncSamSliderControls(host);
};

const destroySamSliderNavigation = (host) => {
  host.removeEventListener('click', host.handleControlClick, true);
  host.track?.removeEventListener('scroll', host.handleTrackScroll);
  host.track?.removeEventListener('pointerdown', host.handleTrackPointerDown);
  host.track?.removeEventListener('pointermove', host.handleTrackPointerMove);
  host.track?.removeEventListener('wheel', host.handleTrackTakeover);
  window.removeEventListener('pointerup', host.handleTrackPointerEnd);
  window.removeEventListener('pointercancel', host.handleTrackPointerEnd);
  host.isPointerDown = false;
  host.isDragging = false;
  host.trackPointerId = undefined;
  clearSamSliderFallback(host);
  clearSamSliderControlQueue(host);
  host.cancelSlideAnimation?.();
  destroySamSliderLoop(host);
};

const scheduleSamSliderAutoplay = (host) => {
  window.clearTimeout(host.autoplayTimer);
  host.autoplayTimer = undefined;
  const prefersReducedMotion =
    host.autoplayReducedMotionQuery?.matches ??
    window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const canAutoplay =
    host.slides.length > 1 &&
    !host.autoplayPausedByHover &&
    !host.autoplayPausedByFocus &&
    !host.autoplayPausedByPointer &&
    !prefersReducedMotion;
  host.track?.setAttribute('aria-live', canAutoplay ? 'off' : 'polite');
  if (!canAutoplay) return;

  host.autoplayTimer = window.setTimeout(() => {
    const reducedMotionEnabled =
      host.autoplayReducedMotionQuery?.matches ??
      window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (reducedMotionEnabled) {
      pauseSamSliderAutoplay(host);
      return;
    }

    host.querySelector('button[name="next"]')?.click();
    scheduleSamSliderAutoplay(host);
  }, 3000);
};

const pauseSamSliderAutoplay = (host) => {
  window.clearTimeout(host.autoplayTimer);
  host.autoplayTimer = undefined;
  host.track?.setAttribute('aria-live', 'polite');
};

const initSamSliderAutoplay = (host) => {
  host.autoplayPausedByHover = false;
  host.autoplayPausedByFocus = false;
  host.autoplayPausedByPointer = false;
  host.autoplayReducedMotionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
  host.handleAutoplayMouseEnter = () => {
    host.autoplayPausedByHover = true;
    pauseSamSliderAutoplay(host);
  };
  host.handleAutoplayMouseLeave = () => {
    host.autoplayPausedByHover = false;
    scheduleSamSliderAutoplay(host);
  };
  host.handleAutoplayFocusIn = () => {
    host.autoplayPausedByFocus = true;
    pauseSamSliderAutoplay(host);
  };
  host.handleAutoplayFocusOut = (event) => {
    if (event.relatedTarget && host.contains(event.relatedTarget)) return;
    host.autoplayPausedByFocus = false;
    scheduleSamSliderAutoplay(host);
  };
  host.handleAutoplayReducedMotionChange = (event) => {
    if (event.matches) {
      pauseSamSliderAutoplay(host);
      return;
    }
    scheduleSamSliderAutoplay(host);
  };
  host.addEventListener('mouseenter', host.handleAutoplayMouseEnter);
  host.addEventListener('mouseleave', host.handleAutoplayMouseLeave);
  host.addEventListener('focusin', host.handleAutoplayFocusIn);
  host.addEventListener('focusout', host.handleAutoplayFocusOut);
  host.autoplayReducedMotionQuery.addEventListener('change', host.handleAutoplayReducedMotionChange);
  scheduleSamSliderAutoplay(host);
};

const destroySamSliderAutoplay = (host) => {
  host.removeEventListener('mouseenter', host.handleAutoplayMouseEnter);
  host.removeEventListener('mouseleave', host.handleAutoplayMouseLeave);
  host.removeEventListener('focusin', host.handleAutoplayFocusIn);
  host.removeEventListener('focusout', host.handleAutoplayFocusOut);
  host.autoplayReducedMotionQuery?.removeEventListener('change', host.handleAutoplayReducedMotionChange);
  host.autoplayPausedByPointer = false;
  pauseSamSliderAutoplay(host);
  host.autoplayReducedMotionQuery = undefined;
};

class SamPaddedSlider extends HTMLElement {
  connectedCallback() {
    this.sliderComponent = this.querySelector('slider-component');
    this.track = this.querySelector('[id^="Slider-"]');
    this.currentElement = this.querySelector('.slider-counter--current');
    this.totalElement = this.querySelector('.slider-counter--total');
    this.progressElement = this.querySelector('.sam-best-sellers__progress');
    this.controlFallbackDelay = 1100;
    this.slides = [...this.querySelectorAll('[id^="Slide-"]')];
    this.handleSlideChange = () => this.syncFromTrack(getSamNearestSlideIndex(this));
    this.handleKeydown = (event) => this.onKeydown(event);
    this.handleWindowResize = () => this.onResize();
    this.sliderComponent?.addEventListener('slideChanged', this.handleSlideChange);
    this.track?.addEventListener('keydown', this.handleKeydown);
    window.addEventListener('resize', this.handleWindowResize, { passive: true });
    this.counterObserver = new MutationObserver(() => this.syncFromTrack(getSamNearestSlideIndex(this)));
    if (this.currentElement) this.counterObserver.observe(this.currentElement, { childList: true, subtree: true });
    if (this.totalElement) this.counterObserver.observe(this.totalElement, { childList: true, subtree: true });
    this.syncFromTrack(0);
    initSamSliderNavigation(this);
    initSamSliderAutoplay(this);
  }

  disconnectedCallback() {
    this.sliderComponent?.removeEventListener('slideChanged', this.handleSlideChange);
    this.track?.removeEventListener('keydown', this.handleKeydown);
    window.removeEventListener('resize', this.handleWindowResize);
    destroySamSliderNavigation(this);
    destroySamSliderAutoplay(this);
    window.cancelAnimationFrame(this.resizeFrame);
    this.resizeTargetIndex = undefined;
    this.counterObserver?.disconnect();
  }

  onResize() {
    if (this.loopReady === false) return;
    const currentIndex = Number.isInteger(this.pendingSlideIndex)
      ? this.pendingSlideIndex
      : Number.isInteger(this.resizeTargetIndex)
        ? this.resizeTargetIndex
        : getSamNearestSlideIndex(this);
    this.resizeTargetIndex = currentIndex;
    this.cancelSlideAnimation();
    window.clearTimeout(this.loopSettleTimer);
    clearSamSliderFallback(this);
    clearSamSliderControlQueue(this);
    this.sliderComponent?.initPages?.();
    window.cancelAnimationFrame(this.resizeFrame);
    this.resizeFrame = window.requestAnimationFrame(() => {
      this.resizeFrame = undefined;
      const targetIndex = this.resizeTargetIndex;
      resetSamSliderPosition(this, getSamSliderPosition(this, targetIndex), () => {
        this.resizeTargetIndex = undefined;
        this.sliderComponent?.update?.();
        this.syncFromTrack(targetIndex);
        runSamSliderQueuedControl(this);
      });
    });
  }

  setSlidePosition(index, position, behavior) {
    this.cancelSlideAnimation();
    if (behavior === 'instant' || window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      this.track.scrollTo({ left: position, behavior: 'instant' });
      return;
    }
    this.classList.add('is-programmatic');

    const startPosition = this.track.scrollLeft;
    const distance = position - startPosition;
    const duration = 950;
    const startTime = performance.now();
    const step = (now) => {
      const elapsed = Math.min((now - startTime) / duration, 1);
      const eased =
        elapsed < 0.5 ? 4 * elapsed * elapsed * elapsed : 1 - Math.pow(-2 * elapsed + 2, 3) / 2;
      this.track.scrollTo({ left: startPosition + distance * eased, behavior: 'instant' });
      if (elapsed < 1) {
        this.slideAnimationFrame = window.requestAnimationFrame(step);
        return;
      }
      this.slideAnimationFrame = undefined;
      this.classList.remove('is-programmatic');
      this.sliderComponent?.update?.();
    };
    this.slideAnimationFrame = window.requestAnimationFrame(step);
  }

  cancelSlideAnimation() {
    window.cancelAnimationFrame(this.slideAnimationFrame);
    this.slideAnimationFrame = undefined;
    this.classList.remove('is-programmatic');
  }

  syncFromTrack(logicalIndex) {
    this.updateCounter(logicalIndex + 1);
  }

  updateProgress(logicalIndex) {
    const denominator = Math.max(this.slides.length - 1, 1);
    const progress = Math.min(Math.max(logicalIndex / denominator, 0), 1);
    const currentPage = logicalIndex + 1;
    this.style.setProperty('--sam-progress', String(progress));
    this.progressElement?.setAttribute('aria-valuenow', String(currentPage));
    this.progressElement?.setAttribute('aria-valuetext', `Slide ${currentPage} of ${this.slides.length}`);
  }

  updateCounter(currentPage) {
    const boundedPage = Math.min(Math.max(currentPage, 1), this.slides.length);
    const paddedCurrent = String(boundedPage).padStart(2, '0');
    const paddedTotal = String(this.slides.length).padStart(2, '0');
    if (this.currentElement && this.currentElement.textContent !== paddedCurrent) {
      this.currentElement.textContent = paddedCurrent;
    }
    if (this.totalElement && this.totalElement.textContent !== paddedTotal) {
      this.totalElement.textContent = paddedTotal;
    }
    syncSamSliderControls(this);
    this.updateProgress(boundedPage - 1);
  }


  onKeydown(event) {
    if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') return;
    event.preventDefault();
    const buttonName = event.key === 'ArrowRight' ? 'next' : 'previous';
    this.querySelector(`button[name="${buttonName}"]`)?.click();
  }
}

if (!customElements.get('sam-padded-slider')) customElements.define('sam-padded-slider', SamPaddedSlider);

class SamCoverflow extends HTMLElement {
  connectedCallback() {
    this.sliderComponent = this.querySelector('slider-component');
    this.track = this.querySelector('[id^="Slider-"]');
    this.slides = [...this.querySelectorAll('.sam-coverflow__slide')];
    this.currentElement = this.querySelector('.slider-counter--current');
    this.totalElement = this.querySelector('.slider-counter--total');
    this.progressElement = this.querySelector('.sam-coverflow__progress');
    this.controlFallbackDelay = 1400;
    this.loopCloneCount = 3;
    this.loopPreloadImages = true;
    this.handleSlideChange = () => this.syncFromTrack(getSamNearestSlideIndex(this));
    this.handleKeydown = (event) => this.onKeydown(event);
    this.handleWindowResize = () => this.onResize();
    this.handlePointerDown = (event) => this.onPointerDown(event);
    this.handlePointerMove = (event) => this.onPointerMove(event);
    this.handlePointerUp = (event) => this.finishDrag(event);
    this.handleDragStart = (event) => event.preventDefault();
    this.handleTrackClick = (event) => this.onTrackClick(event);
    this.handleBlockSelect = (event) => this.onBlockSelect(event);
    this.sliderComponent?.addEventListener('slideChanged', this.handleSlideChange);
    this.track?.addEventListener('keydown', this.handleKeydown);
    this.counterObserver = new MutationObserver(() => this.syncFromTrack(getSamNearestSlideIndex(this)));
    if (this.currentElement) this.counterObserver.observe(this.currentElement, { childList: true, subtree: true });
    if (this.totalElement) this.counterObserver.observe(this.totalElement, { childList: true, subtree: true });
    this.track?.addEventListener('pointerdown', this.handlePointerDown);
    this.track?.addEventListener('pointermove', this.handlePointerMove);
    this.track?.addEventListener('dragstart', this.handleDragStart);
    window.addEventListener('pointerup', this.handlePointerUp);
    window.addEventListener('pointercancel', this.handlePointerUp);
    this.track?.addEventListener('click', this.handleTrackClick, true);
    document.addEventListener('shopify:block:select', this.handleBlockSelect);
    window.addEventListener('resize', this.handleWindowResize, { passive: true });
    this.syncFromTrack(0);
    initSamSliderNavigation(this);
    initSamSliderAutoplay(this);
  }

  disconnectedCallback() {
    this.sliderComponent?.removeEventListener('slideChanged', this.handleSlideChange);
    this.track?.removeEventListener('keydown', this.handleKeydown);
    this.track?.removeEventListener('pointerdown', this.handlePointerDown);
    this.track?.removeEventListener('pointermove', this.handlePointerMove);
    this.track?.removeEventListener('dragstart', this.handleDragStart);
    window.removeEventListener('pointerup', this.handlePointerUp);
    window.removeEventListener('pointercancel', this.handlePointerUp);
    this.track?.removeEventListener('click', this.handleTrackClick, true);
    document.removeEventListener('shopify:block:select', this.handleBlockSelect);
    window.removeEventListener('resize', this.handleWindowResize);
    destroySamSliderNavigation(this);
    destroySamSliderAutoplay(this);
    window.clearTimeout(this.dragClickTimer);
    window.cancelAnimationFrame(this.resizeFrame);
    window.cancelAnimationFrame(this.blockSelectFrame);
    this.coverflowPointerId = undefined;
    this.didDrag = false;
    this.suppressTrackClick = false;
    this.classList.remove('is-dragging');
    this.counterObserver?.disconnect();
  }

  setSlidePosition(_index, position, behavior) {
    this.track.scrollTo({ left: position, behavior });
  }

  onResize() {
    if (this.loopReady === false) return;
    const currentIndex = Number.isInteger(this.pendingSlideIndex)
      ? this.pendingSlideIndex
      : getSamNearestSlideIndex(this);
    this.cancelSlideAnimation?.();
    window.clearTimeout(this.loopSettleTimer);
    clearSamSliderFallback(this);
    clearSamSliderControlQueue(this);
    this.sliderComponent?.initPages();
    window.cancelAnimationFrame(this.resizeFrame);
    this.resizeFrame = window.requestAnimationFrame(() => {
      this.resizeFrame = undefined;
      resetSamSliderPosition(this, getSamSliderPosition(this, currentIndex), () => {
        this.sliderComponent?.update?.();
        this.syncFromTrack(currentIndex);
        runSamSliderQueuedControl(this);
      });
    });
  }

  onPointerDown(event) {
    if (event.button !== 0 || event.isPrimary === false || event.pointerType === 'touch' || !this.track) return;
    this.coverflowPointerId = event.pointerId;
    this.didDrag = false;
    this.dragStartX = event.clientX;
    this.dragStartPosition = this.track.scrollLeft;
  }

  onPointerMove(event) {
    if (event.pointerId !== this.coverflowPointerId || !this.track) return;
    const delta = this.dragStartX - event.clientX;
    if (!this.didDrag) {
      if (Math.abs(delta) <= 4) return;
      this.didDrag = true;
      this.classList.add('is-dragging');
      this.track.setPointerCapture?.(event.pointerId);
    }
    const maximumPosition = Math.max(this.track.scrollWidth - this.track.clientWidth, 0);
    const position = Math.min(Math.max(this.dragStartPosition + delta, 0), maximumPosition);
    this.track.scrollTo({ left: position, behavior: 'instant' });
    this.sliderComponent?.update?.();
    event.preventDefault();
  }

  finishDrag(event) {
    if (event.pointerId !== this.coverflowPointerId) return;
    this.coverflowPointerId = undefined;
    const didDrag = this.didDrag;
    this.didDrag = false;
    this.classList.remove('is-dragging');
    if (this.track?.hasPointerCapture?.(event.pointerId)) this.track.releasePointerCapture(event.pointerId);
    if (!didDrag) return;

    const target = getSamNearestLoopTarget(this);
    const behavior = window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'instant' : 'smooth';
    moveSamSliderToTarget(this, target, behavior);
    if (event.type === 'pointercancel') {
      window.clearTimeout(this.dragClickTimer);
      this.suppressTrackClick = false;
      return;
    }
    this.suppressTrackClick = true;
    window.clearTimeout(this.dragClickTimer);
    this.dragClickTimer = window.setTimeout(() => {
      this.suppressTrackClick = false;
    }, 0);
  }

  onTrackClick(event) {
    if (!this.suppressTrackClick) return;
    event.preventDefault();
    event.stopPropagation();
    this.suppressTrackClick = false;
  }

  onBlockSelect(event) {
    const selectedSlide = event.target.closest?.('.sam-coverflow__slide');
    if (!selectedSlide || !this.contains(selectedSlide) || selectedSlide.classList.contains('sam-slider-clone')) return;
    const selectedIndex = this.slides.indexOf(selectedSlide);
    if (selectedIndex < 0) return;

    this.handleTrackTakeover?.();
    window.cancelAnimationFrame(this.blockSelectFrame);
    this.blockSelectFrame = window.requestAnimationFrame(() => {
      this.blockSelectFrame = undefined;
      this.setSlidePosition(selectedIndex, getSamSliderPosition(this, selectedIndex), 'instant');
      this.sliderComponent?.update?.();
      this.syncFromTrack(selectedIndex);
    });
  }

  syncFromTrack(logicalIndex) {
    const boundedIndex = Math.min(Math.max(logicalIndex, 0), this.slides.length - 1);
    const slideCount = this.slides.length;
    const positionedSlides = this.loopSlides || this.slides;
    positionedSlides.forEach((slide) => {
      const logicalIndexForSlide = getSamLogicalIndex(this, slide);
      let relativePosition = logicalIndexForSlide - boundedIndex;
      if (relativePosition > slideCount / 2) relativePosition -= slideCount;
      if (relativePosition < slideCount / -2) relativePosition += slideCount;
      slide.dataset.samPosition = String(relativePosition);
    });
    this.loopSlides?.forEach((slide) => {
      slide.classList.toggle('is-active', getSamLogicalIndex(this, slide) === boundedIndex);
    });
    this.slides.forEach((slide, index) => {
      const active = index === boundedIndex;
      slide.classList.toggle('is-active', active);
      slide.setAttribute('aria-current', active ? 'true' : 'false');
      slide.querySelectorAll('a').forEach((link) => {
        link.tabIndex = active ? 0 : -1;
      });
    });
    syncSamSliderControls(this);
    this.padCounters(boundedIndex + 1);
    this.updateProgress(boundedIndex);
  }

  padCounters(currentPage) {
    const boundedPage = Math.min(Math.max(currentPage, 1), this.slides.length);
    const paddedCurrent = String(boundedPage).padStart(2, '0');
    const paddedTotal = String(this.slides.length).padStart(2, '0');
    if (this.currentElement && this.currentElement.textContent !== paddedCurrent) {
      this.currentElement.textContent = paddedCurrent;
    }
    if (this.totalElement && this.totalElement.textContent !== paddedTotal) {
      this.totalElement.textContent = paddedTotal;
    }
  }

  updateProgress(logicalIndex) {
    const denominator = Math.max(this.slides.length - 1, 1);
    const progress = Math.min(Math.max(logicalIndex / denominator, 0), 1);
    const currentPage = logicalIndex + 1;
    this.style.setProperty('--sam-progress', String(progress));
    this.progressElement?.setAttribute('aria-valuenow', String(currentPage));
    this.progressElement?.setAttribute('aria-valuetext', `Slide ${currentPage} of ${this.slides.length}`);
  }

  onKeydown(event) {
    if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') return;
    event.preventDefault();
    const buttonName = event.key === 'ArrowRight' ? 'next' : 'previous';
    this.querySelector(`button[name="${buttonName}"]`)?.click();
  }
}

if (!customElements.get('sam-coverflow')) customElements.define('sam-coverflow', SamCoverflow);

const initSamTheme = () => {
  initSamFaq();
  initSamMarketingPopup();
  initSamProductPurchase();
  initSamMinimumOrderCleanup();
  initSamProductGallery();
};

document.addEventListener('DOMContentLoaded', initSamTheme, { once: true });
document.addEventListener('shopify:section:load', (event) => {
  initSamFaq(event.target);
  initSamProductPurchase(event.target);
  initSamProductGallery(event.target);
});
